import type { CSSProperties, ReactNode } from 'react';
import { T, card, h3, h3Note, th, td, tnum } from '../../../../theme/tokens';

/* The shared furniture of the screen: a card, its title, and the three table parts.
   Written once so eleven panels agree on padding, border weight and header colour —
   they previously each carried their own copy and had drifted by a pixel or two,
   which is exactly the kind of thing that reads as "unfinished" without anyone
   being able to say why. */

export function Card({ children, flush, style }: {
  children: ReactNode;
  /* `flush` drops the padding for cards whose whole body is a table — a table needs
     to run to the card's edge, or its header band floats in a white margin. */
  flush?: boolean;
  style?: CSSProperties;
}) {
  return (
    <div style={{ ...card, padding: flush ? 0 : '16px 18px', overflow: 'hidden', ...style }}>
      {children}
    </div>
  );
}

/* A question mark that carries the definition. The marker is on the number itself
   and not in a document: a definition written in a document gets read once, a
   question mark beside the figure gets asked every time someone looks
   (Ohad, 2026-10-07). */
export function Ask({ children }: { children: string }) {
  return (
    <span
      title={children}
      role="img"
      aria-label={`הסבר: ${children}`}
      style={{
        marginInlineStart: 2, cursor: 'help', flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 15, height: 15, borderRadius: '50%',
        background: T.orangeTint, color: T.orange,
        fontSize: 10, fontWeight: 700, lineHeight: 1,
      }}
    >
      ?
    </span>
  );
}

export function Title({ children, note, ask, flush, badge }: {
  children: ReactNode; note?: string; ask?: string; flush?: boolean;
  /* Trailing slot, pushed to the far end of the title line. Used by the
     "this filter did not reach this panel" marker, which has to be visible on the
     panel it qualifies rather than collected in a legend nobody reads. */
  badge?: ReactNode;
}) {
  return (
    <div style={{ ...h3, ...(flush ? { padding: '16px 18px 0', marginBottom: 12 } : {}) }}>
      <span>{children}</span>
      {note && <span style={h3Note}>· {note}</span>}
      {ask && <Ask>{ask}</Ask>}
      {badge && <span style={{ marginInlineStart: 'auto' }}>{badge}</span>}
    </div>
  );
}

/* Tables scroll inside their own box. The page body must never scroll sideways —
   a nine-column table on a laptop is the one thing that forces it to. */
export function Scroll({ children, min }: { children: ReactNode; min?: number }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: min ?? 360 }}>
        {children}
      </table>
    </div>
  );
}

type Align = 'start' | 'center' | 'end';
/* `start`/`end` rather than right/left: in RTL the label column starts on the right
   and the money columns end on the left, and writing it in physical directions is
   how a table ends up mirrored on the one screen somebody views in English. */
const textAlign = (a: Align): CSSProperties['textAlign'] =>
  a === 'start' ? 'right' : a === 'end' ? 'left' : 'center';

export function Th({ children, align = 'start' }: { children: ReactNode; align?: Align }) {
  return <th style={{ ...th, textAlign: textAlign(align) }}>{children}</th>;
}

export function Td({ children, align = 'start', strong, soft, color, colSpan, title, style }: {
  children: ReactNode; align?: Align; strong?: boolean; soft?: boolean;
  color?: string; colSpan?: number; title?: string; style?: CSSProperties;
}) {
  return (
    <td
      colSpan={colSpan}
      title={title}
      style={{
        ...td, ...tnum, textAlign: textAlign(align),
        fontWeight: strong ? 700 : 400,
        color: color ?? (soft ? T.inkSoft : T.ink),
        ...style,
      }}
    >
      {children}
    </td>
  );
}

/* The summary row. Heavier top rule and a tinted ground, so the eye can tell a
   total from one more data row without reading the word. */
export function TotalRow({ children }: { children: ReactNode }) {
  return (
    <tr style={{ background: T.bg, fontWeight: 700, borderTop: `2px solid ${T.border}` }}>
      {children}
    </tr>
  );
}

export function Row({ children, tint }: { children: ReactNode; tint?: string }) {
  return <tr className="tz-row" style={tint ? { background: tint } : undefined}>{children}</tr>;
}

/* A note under a panel that qualifies what was just shown. Small, grey, and always
   the last thing in the card — never floating between the title and the data. */
export function Footnote({ children, pad }: { children: ReactNode; pad?: boolean }) {
  return (
    <div style={{
      fontSize: 11.5, color: T.inkSoft, lineHeight: 1.6,
      marginTop: pad ? 0 : 10,
      padding: pad ? '11px 18px' : undefined,
      borderTop: pad ? `1px solid ${T.borderSoft}` : undefined,
    }}>
      {children}
    </div>
  );
}

/* A measured number that is NOT available yet, named with the reason. A blocked
   panel says why instead of showing a plausible figure — a wrong EBITDA is worse
   than a missing one (Ohad, 2026-10-06). */
export function Blocked({ title, why }: { title: string; why: string }) {
  return (
    <div style={{
      border: `1px dashed ${T.border}`, borderRadius: T.radius,
      background: T.bg, padding: '18px 18px',
    }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.inkSoft, marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 12, color: T.inkSoft, lineHeight: 1.65 }}>{why}</div>
    </div>
  );
}
